// Avatar system backed by DiceBear's Avataaars style (open-source, MIT
// license, https://www.dicebear.com). Renders via their public SVG API —
// AvatarSvg just builds a URL from the customization state and drops it
// into an <img>, so the schema below is our own simplified vocabulary
// mapped onto Avataaars' real option names (see buildDicebearUrl). No
// option here needs a build step or a hosted asset of our own.
//
// A few axes the old hand-drawn system had aren't available in Avataaars
// and were dropped rather than faked: face shape, eye/nose size, and
// earrings. Beard and mustache are exposed separately here but map to a
// single underlying "facialHair" slot, so picking one clears the other
// (matches how Avataaars actually draws facial hair).
//
// Eye color IS faked, carefully: Avataaars has no iris-color parameter at
// all, but this app always renders the "default" eyes variant (see
// buildDicebearUrl), and that variant's two pupils are always drawn as one
// exact, uniquely-identifiable path: `fill="#000" fill-opacity=".6"` (only
// match in the document, verified across many option combinations). When a
// non-default eye color is picked, AvatarSvg fetches the raw SVG text,
// string-replaces that one signature, and renders the patched SVG as a
// data URI instead of the plain remote <img src>. This only runs for the
// opt-in case — default ("preto") stays a zero-cost <img src=URL> exactly
// like every other option here.

const DICEBEAR_AVATAAARS_BASE = "https://api.dicebear.com/9.x/avataaars/svg";

const AVATAR_SKIN_TONES = [
  { id: "muitoClara", color: "#FFDBB4", premium: false },
  { id: "dourada", color: "#F8D25C", premium: false },
  { id: "clara", color: "#EDB98A", premium: false },
  { id: "morenaClara", color: "#FD9841", premium: false },
  { id: "morena", color: "#D08B5B", premium: false },
  { id: "morenaEscura", color: "#AE5D29", premium: false },
  { id: "negraRetinta", color: "#614335", premium: false },
];
const AVATAR_HAIR_STYLES = [
  { id: "raspado", key: "hairStyleRaspado", premium: false, top: "shortFlat" },
  { id: "careca", key: "hairStyleCareca", premium: false, top: null },
  { id: "social", key: "hairStyleSocial", premium: false, top: "shortRound" },
  { id: "cacheado", key: "hairStyleCacheado", premium: false, top: "curly" },
  { id: "cacheadoLongo", key: "hairStyleCacheadoLongo", premium: false, top: "curvy" },
  { id: "cacheadoVolumoso", key: "hairStyleCacheadoVolumoso", premium: false, top: "bigHair" },
  { id: "afro", key: "hairStyleAfro", premium: false, top: "fro" },
  { id: "raboDeCavalo", key: "hairStyleRabo", premium: false, top: "bun" },
  { id: "soltoLongo", key: "hairStyleSolto", premium: false, top: "straight02" },
  { id: "chapeu", key: "hairStyleChapeu", premium: false, top: "hat", isHat: true },
  { id: "gorro", key: "hairStyleGorro", premium: false, top: "winterHat1", isHat: true },
  { id: "hijab", key: "hairStyleHijab", premium: false, top: "hijab", isHat: true },
  { id: "turbante", key: "hairStyleTurbante", premium: false, top: "turban", isHat: true },
];
const AVATAR_HAIR_COLORS = [
  { id: "preto", color: "#2C1B18", premium: false },
  { id: "castanhoEscuro", color: "#4A312C", premium: false },
  { id: "castanho", color: "#724133", premium: false },
  { id: "castanhoDourado", color: "#B58143", premium: false },
  { id: "ruivo", color: "#A55728", premium: false },
  { id: "ruivoVivo", color: "#C93305", premium: false },
  { id: "loiro", color: "#D6B370", premium: false },
  { id: "loiroPlatinado", color: "#ECDCBF", premium: false },
  { id: "grisalho", color: "#E8E1E1", premium: false },
  { id: "rosa", color: "#F59797", premium: false },
];
const AVATAR_EYE_COLORS = [
  { id: "preto", color: "#1A1A1A", premium: false },
  { id: "castanho", color: "#6B4423", premium: false },
  { id: "verde", color: "#2E7D4F", premium: false },
  { id: "ambar", color: "#B8860B", premium: false },
  { id: "azul", color: "#4A7DC4", premium: false },
];
// The exact path geometry Avataaars uses to draw both pupils in the
// "default" eyes variant — see the file-header comment. Matching on the
// fill+opacity alone isn't safe: at least one hairstyle ("bigHair") draws
// an unrelated shading detail with that exact same fill/opacity pair, so
// this pins down the literal circle coordinates instead, which stayed a
// unique match across every hairstyle/accessory/facial-hair combo tested.
const EYE_PUPIL_SIGNATURE = 'd="M36 22a6 6 0 1 1-12 0 6 6 0 0 1 12 0ZM88 22a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" fill="#000" fill-opacity=".6"';
// Shared accent palette for accessory colors (hat/glasses) — plain swatch
// rows like clothingColor, no premium gate.
const AVATAR_ACCENT_COLORS = ["#262E33", "#4A7DC4", "#3E7A4E", "#A62C2C", "#8A6A1E", "#5C6B77"];
const AVATAR_EYEBROWS = [
  { id: "medias", key: "browMedias", premium: false, value: "defaultNatural" },
  { id: "finas", key: "browFinas", premium: false, value: "flatNatural" },
  { id: "grossas", key: "browGrossas", premium: false, value: "unibrowNatural" },
];
const AVATAR_MOUTHS = [
  // Avataaars' "default" mouth renders as a dark open-mouth shape (by
  // design, meant for a surprised/talking look) — it reads as a plain
  // black blob at avatar sizes, so "neutral" uses "serious" (a thin closed
  // line) instead, which actually looks like a normal resting mouth.
  { id: "neutra", key: "mouthNeutra", premium: false, value: "serious" },
  { id: "sorrindo", key: "mouthSorrindo", premium: false, value: "smile" },
  { id: "pequena", key: "mouthPequena", premium: false, value: "twinkle" },
];
const AVATAR_BEARDS = [
  { id: "nenhuma", key: "beardNenhuma", premium: false, value: null },
  // Avataaars only varies beard density (light/medium/majestic), not
  // shape — there's no true chin-only goatee, so this is labeled as a
  // sparse beard rather than promising a shape it can't draw.
  { id: "rala", key: "beardRala", premium: false, value: "beardLight" },
  { id: "curta", key: "beardCurta", premium: false, value: "beardMedium" },
  { id: "cheia", key: "beardCheia", premium: false, value: "beardMajestic" },
];
const AVATAR_MUSTACHES = [
  { id: "nenhum", key: "mustacheNenhum", premium: false, value: null },
  { id: "fino", key: "mustacheFino", premium: false, value: "moustacheFancy" },
  { id: "chevron", key: "mustacheChevron", premium: false, value: "moustacheMagnum" },
];
const AVATAR_GLASSES = [
  { id: "nenhum", key: "glassesNenhum", premium: false, value: null },
  { id: "redondo", key: "glassesRedondo", premium: false, value: "round" },
  { id: "quadrado", key: "glassesQuadrado", premium: false, value: "prescription02" },
  { id: "aviador", key: "glassesAviador", premium: false, value: "sunglasses" },
];
const AVATAR_CLOTHING = [
  { id: "camiseta", key: "clothingCamiseta", premium: false, value: "shirtCrewNeck" },
  { id: "polo", key: "clothingPolo", premium: false, value: "collarAndSweater" },
  { id: "jaqueta", key: "clothingJaqueta", premium: false, value: "blazerAndSweater" },
  { id: "moletom", key: "clothingMoletom", premium: false, value: "hoodie" },
];
const AVATAR_CLOTHING_COLORS = ["#4A7DC4", "#3E7A4E", "#A62C2C", "#514B7A", "#5C6B77", "#8A6A1E"];
const AVATAR_BACKGROUNDS = [
  { id: "azul", color: "#22344A", premium: false },
  { id: "verde", color: "#1F3D33", premium: false },
  { id: "vermelho", color: "#4A2323", premium: false },
  { id: "cinza", color: "#3A3F45", premium: false },
  { id: "laranja", color: "#4A3520", premium: false },
  { id: "roxo", color: "#332049", premium: false },
  { id: "amarelo", color: "#4A4220", premium: false },
  { id: "turquesa", color: "#1D4448", premium: false },
];
// "Special outfit" is a community trust rank marker (free, unlocked by
// earning it — gated by minTrustLevel, matching the 4 levels in
// ProfileScreen's trust system). All 4 tiers use the same suit silhouette
// ("blazerAndShirt" — Avataaars' closest thing to a proper terno) so the
// *color* is what reads as the tier, plus a small lapel-pin overlay
// (badgeColor/badgeIcon, rendered by AvatarSvg) for an extra, unambiguous
// "this is an earned rank" marker that a plain clothing-color swatch
// wouldn't give.
const AVATAR_SPECIAL_OUTFITS = [
  { id: "none", key: "uniformNone", color: null, clothing: null, premium: false, minTrustLevel: 0 },
  { id: "rank0", key: "rankObservador", color: "#F7F7F5", badgeColor: "#9AA0A6", badgeIcon: "eye", clothing: "blazerAndShirt", premium: false, minTrustLevel: 0 },
  { id: "rank1", key: "rankMorador", color: "#2F5C9E", badgeColor: "#4A7DC4", badgeIcon: "badge-check", clothing: "blazerAndShirt", premium: false, minTrustLevel: 1 },
  { id: "rank2", key: "rankSentinela", color: "#2E7D4F", badgeColor: "#3E7A4E", badgeIcon: "shield", clothing: "blazerAndShirt", premium: false, minTrustLevel: 2 },
  { id: "rank3", key: "rankGuardiao", color: "#D4AF37", badgeColor: "#D4AF37", badgeIcon: "crown", clothing: "blazerAndShirt", premium: false, minTrustLevel: 3 },
];

const DEFAULT_AVATAR_FACE = {
  skin: "clara",
  hairStyle: "social",
  hairColor: "castanho",
  hatColor: AVATAR_ACCENT_COLORS[0],
  eyeColor: "preto",
  eyebrows: "medias",
  mouth: "neutra",
  beard: "nenhuma",
  mustache: "nenhum",
  glasses: "nenhum",
  glassesColor: AVATAR_ACCENT_COLORS[0],
  clothing: "camiseta",
  clothingColor: AVATAR_CLOTHING_COLORS[0],
  specialUniform: "none",
  background: "azul",
};

function findOpt(list, id, fallback) {
  return list.find((o) => o.id === id) || fallback || list[0];
}

function hex(c) {
  return String(c || "").replace("#", "");
}

function buildDicebearUrl(face, size) {
  const f = { ...DEFAULT_AVATAR_FACE, ...(face || {}) };
  const hairStyle = findOpt(AVATAR_HAIR_STYLES, f.hairStyle);
  const hairColor = hex(findOpt(AVATAR_HAIR_COLORS, f.hairColor).color);
  const uniform = findOpt(AVATAR_SPECIAL_OUTFITS, f.specialUniform);
  const hasUniform = uniform.id !== "none";
  const clothing = hasUniform ? uniform.clothing : findOpt(AVATAR_CLOTHING, f.clothing).value;
  const clothesColor = hex(hasUniform ? uniform.color : f.clothingColor);
  // A beard and a mustache both map to Avataaars' single "facialHair" slot
  // — beard takes priority if both are somehow set.
  const beard = findOpt(AVATAR_BEARDS, f.beard).value;
  const mustache = findOpt(AVATAR_MUSTACHES, f.mustache).value;
  const facialHair = beard || mustache;
  const glasses = findOpt(AVATAR_GLASSES, f.glasses).value;

  const params = new URLSearchParams();
  params.set("seed", "alerta-al-static");
  params.set("skinColor", hex(findOpt(AVATAR_SKIN_TONES, f.skin).color));
  params.set("backgroundColor", hex(findOpt(AVATAR_BACKGROUNDS, f.background).color));
  params.set("hairColor", hairColor);
  params.set("eyebrows", findOpt(AVATAR_EYEBROWS, f.eyebrows).value);
  params.set("eyes", "default");
  params.set("mouth", findOpt(AVATAR_MOUTHS, f.mouth).value);
  params.set("clothing", clothing);
  params.set("clothesColor", clothesColor);
  if (hairStyle.top) {
    params.set("top", hairStyle.top);
    params.set("topProbability", "100");
    if (hairStyle.isHat) params.set("hatColor", hex(f.hatColor));
  } else {
    params.set("topProbability", "0");
  }
  if (facialHair) {
    params.set("facialHair", facialHair);
    params.set("facialHairColor", hairColor);
    params.set("facialHairProbability", "100");
  } else {
    params.set("facialHairProbability", "0");
  }
  if (glasses) {
    params.set("accessories", glasses);
    params.set("accessoriesColor", hex(f.glassesColor));
    params.set("accessoriesProbability", "100");
  } else {
    params.set("accessoriesProbability", "0");
  }
  if (size) params.set("size", String(Math.round(size * 2)));
  return `${DICEBEAR_AVATAAARS_BASE}?${params.toString()}`;
}

function getRankBadge(face) {
  const f = { ...DEFAULT_AVATAR_FACE, ...(face || {}) };
  const uniform = findOpt(AVATAR_SPECIAL_OUTFITS, f.specialUniform);
  return uniform.badgeColor ? { color: uniform.badgeColor, icon: uniform.badgeIcon } : null;
}

function AvatarSvg({ face, size }) {
  const s = size || 40;
  const f = { ...DEFAULT_AVATAR_FACE, ...(face || {}) };
  const badge = getRankBadge(face);
  // The pin looks right at profile-preview scale but overwhelms a small
  // header-chip/list avatar (there just isn't room to shrink it and keep
  // the icon legible), so it's simply omitted below this size rather than
  // scaled down further.
  const showBadge = badge && s >= 56;
  const badgeSize = s * 0.2;
  const DesignSystem = window.VigiALDesignSystem_ff2dec;
  const Icon = DesignSystem && DesignSystem.Icon;

  const baseUrl = buildDicebearUrl(face, s);
  const eyeColor = findOpt(AVATAR_EYE_COLORS, f.eyeColor);
  const needsEyePatch = eyeColor.id !== "preto";
  const [imgSrc, setImgSrc] = React.useState(baseUrl);

  React.useEffect(() => {
    if (!needsEyePatch) { setImgSrc(baseUrl); return undefined; }
    let cancelled = false;
    fetch(baseUrl)
      .then((r) => r.text())
      .then((svgText) => {
        if (cancelled) return;
        const patched = svgText.replace(EYE_PUPIL_SIGNATURE, `d="M36 22a6 6 0 1 1-12 0 6 6 0 0 1 12 0ZM88 22a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" fill="${eyeColor.color}" fill-opacity="1"`);
        setImgSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(patched)}`);
      })
      .catch(() => { if (!cancelled) setImgSrc(baseUrl); });
    return () => { cancelled = true; };
  }, [baseUrl, needsEyePatch, eyeColor.color]);

  return (
    <div style={{ position: "relative", width: s, height: s, flexShrink: 0 }}>
      <img
        src={imgSrc}
        width={s}
        height={s}
        alt=""
        style={{ borderRadius: "50%", display: "block" }}
      />
      {showBadge && (
        <div
          style={{
            position: "absolute",
            bottom: s * 0.12,
            right: s * 0.04,
            width: badgeSize,
            height: badgeSize,
            borderRadius: "50%",
            background: badge.color,
            border: `${Math.max(1, s * 0.02)}px solid #1A1A1A`,
            boxShadow: "0 1px 2px rgba(0,0,0,.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {Icon && badge.icon && <Icon name={badge.icon} size={badgeSize * 0.6} color="#1A1A1A" />}
        </div>
      )}
    </div>
  );
}

window.AvatarSvg = AvatarSvg;
window.buildDicebearAvatarUrl = buildDicebearUrl;
window.DEFAULT_AVATAR_FACE = DEFAULT_AVATAR_FACE;
window.AVATAR_SKIN_TONES = AVATAR_SKIN_TONES;
window.AVATAR_HAIR_STYLES = AVATAR_HAIR_STYLES;
window.AVATAR_HAIR_COLORS = AVATAR_HAIR_COLORS;
window.AVATAR_ACCENT_COLORS = AVATAR_ACCENT_COLORS;
window.AVATAR_EYE_COLORS = AVATAR_EYE_COLORS;
window.AVATAR_EYEBROWS = AVATAR_EYEBROWS;
window.AVATAR_MOUTHS = AVATAR_MOUTHS;
window.AVATAR_BEARDS = AVATAR_BEARDS;
window.AVATAR_MUSTACHES = AVATAR_MUSTACHES;
window.AVATAR_GLASSES = AVATAR_GLASSES;
window.AVATAR_CLOTHING = AVATAR_CLOTHING;
window.AVATAR_CLOTHING_COLORS = AVATAR_CLOTHING_COLORS;
window.AVATAR_BACKGROUNDS = AVATAR_BACKGROUNDS;
window.AVATAR_SPECIAL_UNIFORMS = AVATAR_SPECIAL_OUTFITS;
